import React, { useEffect, useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { CheckCircle, XCircle, Loader2 } from 'lucide-react';
import { adminApi } from '../lib/api';
import { toast } from 'sonner';

export default function AdminGoogleCallback() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [status, setStatus] = useState('processing'); // processing, success, error
  const [email, setEmail] = useState('');
  const [error, setError] = useState('');

  useEffect(() => {
    const code = searchParams.get('code');
    const adminSecret = sessionStorage.getItem('adminSecret');

    if (!code) {
      setStatus('error');
      setError('No authorization code received from Google');
      return;
    }

    if (!adminSecret) {
      setStatus('error');
      setError('Admin session expired. Please log in again.');
      return;
    }

    // Exchange code for tokens
    const exchangeCode = async () => {
      try {
        const response = await adminApi.adminGoogleCallback(code, adminSecret);
        if (response.data.success) {
          setStatus('success');
          setEmail(response.data.email);
          toast.success('Google account connected successfully!');
        } else {
          throw new Error(response.data.message || 'Failed to connect');
        }
      } catch (err) {
        setStatus('error');
        setError(err.response?.data?.detail || err.message || 'Failed to connect Google account');
        toast.error('Failed to connect Google account');
      }
    };

    exchangeCode();
  }, [searchParams]);

  return (
    <div className="min-h-screen bg-slate-900 flex items-center justify-center p-4">
      <Card className="w-full max-w-md rounded-sm border-slate-700 bg-slate-800">
        <CardHeader className="text-center">
          {status === 'processing' && (
            <>
              <div className="mx-auto h-16 w-16 bg-blue-500 rounded-full flex items-center justify-center mb-4">
                <Loader2 className="h-8 w-8 text-white animate-spin" />
              </div>
              <CardTitle className="text-xl text-white">Connecting Google Account...</CardTitle>
            </>
          )}
          {status === 'success' && (
            <>
              <div className="mx-auto h-16 w-16 bg-green-500 rounded-full flex items-center justify-center mb-4">
                <CheckCircle className="h-8 w-8 text-white" />
              </div>
              <CardTitle className="text-xl text-white">Google Connected!</CardTitle>
            </>
          )}
          {status === 'error' && (
            <>
              <div className="mx-auto h-16 w-16 bg-red-500 rounded-full flex items-center justify-center mb-4">
                <XCircle className="h-8 w-8 text-white" />
              </div>
              <CardTitle className="text-xl text-white">Connection Failed</CardTitle>
            </>
          )}
        </CardHeader>
        <CardContent className="text-center">
          {status === 'processing' && (
            <p className="text-slate-400">Please wait while we connect your Google account...</p>
          )}
          {status === 'success' && (
            <div className="space-y-4">
              <p className="text-slate-300">
                Connected as: <span className="text-green-400 font-medium">{email}</span>
              </p>
              <p className="text-slate-400 text-sm">
                Email notifications for bug reports and contact messages will now be sent through this account.
              </p>
              <Button
                onClick={() => navigate('/admin')}
                className="w-full rounded-sm bg-orange-500 hover:bg-orange-600"
              >
                Return to Admin Dashboard
              </Button>
            </div>
          )}
          {status === 'error' && (
            <div className="space-y-4">
              <p className="text-red-400">{error}</p>
              <Button
                onClick={() => navigate('/admin')}
                variant="outline"
                className="w-full rounded-sm border-slate-600 text-slate-300 hover:bg-slate-700"
              >
                Return to Admin Dashboard
              </Button>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
